/*
 * Hermyne header tone.
 * The sticky header is see-through, so its text has to follow whatever is behind it:
 * white over dark photos or dark sections, black over light ones.
 * This only reads the page and sets one attribute on <header-component>
 * (data-hermyne-invert); the stylesheet does the rest. Nothing else is touched.
 */
(() => {
  const header = document.querySelector('header-component');
  if (!header) return;

  const ROWS = 16;
  const imageCache = new Map();
  let wantWhite = null;

  const luminance = (r, g, b) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

  const colorLuminance = (value) => {
    const m = value.match(/rgba?\(([^)]+)\)/);
    if (!m) return null;
    const parts = m[1].split(/[\s,\/]+/).filter(Boolean).map(parseFloat);
    const alpha = parts.length > 3 ? parts[3] : 1;
    if (alpha < 0.9) return null;
    return luminance(parts[0], parts[1], parts[2]);
  };

  const thumbUrl = (src) => {
    try {
      const url = new URL(src, location.href);
      if (url.pathname.includes('/cdn/shop/')) url.searchParams.set('width', '48');
      return url.href;
    } catch {
      return src;
    }
  };

  /* Row-by-row brightness of an image, from a tiny copy so the full photo is never re-downloaded */
  const imageRows = (src) => {
    if (imageCache.has(src)) return imageCache.get(src);
    imageCache.set(src, null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = ROWS;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, ROWS, ROWS);
        const data = ctx.getImageData(0, 0, ROWS, ROWS).data;
        const rows = [];
        for (let y = 0; y < ROWS; y++) {
          let sum = 0;
          for (let x = 0; x < ROWS; x++) {
            const i = (y * ROWS + x) * 4;
            sum += luminance(data[i], data[i + 1], data[i + 2]);
          }
          rows.push(sum / ROWS);
        }
        imageCache.set(src, rows);
        update();
      } catch {
        imageCache.set(src, false);
      }
    };
    img.onerror = () => imageCache.set(src, false);
    img.src = thumbUrl(src);
    return null;
  };

  const imageLuminance = (el, y) => {
    const src = el.currentSrc || el.src || el.poster;
    if (!src) return null;
    const rows = imageRows(src);
    if (!rows) return null;
    const rect = el.getBoundingClientRect();
    const nw = el.naturalWidth || el.videoWidth || rect.width;
    const nh = el.naturalHeight || el.videoHeight || rect.height;
    if (!rect.height || !nw || !nh) return null;
    const scale = Math.max(rect.width / nw, rect.height / nh);
    const renderedHeight = nh * scale;
    const posY = parseFloat((getComputedStyle(el).objectPosition.split(' ')[1] || '50%')) / 100;
    const offset = (renderedHeight - rect.height) * (Number.isFinite(posY) ? posY : 0.5);
    const rel = (offset + (y - rect.top)) / renderedHeight;
    return rows[Math.min(ROWS - 1, Math.max(0, Math.floor(rel * ROWS)))];
  };

  const luminanceAt = (x, y) => {
    for (const el of document.elementsFromPoint(x, y)) {
      if (header.contains(el)) continue;
      const style = getComputedStyle(el);
      if (style.position === 'fixed') continue;
      /* a slide that shades its own top edge always takes white header text */
      if (el instanceof HTMLElement && el.dataset.hermyneScrim !== undefined) return 0.15;
      if (el.tagName === 'IMG' || el.tagName === 'VIDEO') {
        const value = imageLuminance(el, y);
        if (value !== null) return value;
        continue;
      }
      const value = colorLuminance(style.backgroundColor);
      if (value !== null) return value;
    }
    return colorLuminance(getComputedStyle(document.body).backgroundColor) ?? 1;
  };

  function update() {
    if (header.querySelector('.menu-list__link[aria-expanded="true"]')) return;
    const rect = header.getBoundingClientRect();
    if (!rect.height) return;
    const y = rect.top + rect.height / 2;
    const xs = [0.12, 0.5, 0.88].map((f) => f * innerWidth);
    const average = xs.reduce((sum, x) => sum + luminanceAt(x, y), 0) / xs.length;

    /* A little hysteresis so the text does not flicker at the edge of a photo */
    if (wantWhite === null) wantWhite = average < 0.46;
    else if (wantWhite && average > 0.52) wantWhite = false;
    else if (!wantWhite && average < 0.42) wantWhite = true;

    /* What the theme is drawing right now: white text only while the header is clear over the hero */
    const themeIsWhite = header.hasAttribute('transparent') && header.dataset.stickyState !== 'active';
    /* Only write when something changed: the theme watches this element while a menu is open */
    const invert = wantWhite !== themeIsWhite;
    const tone = wantWhite ? 'dark' : 'light';
    if (header.hasAttribute('data-hermyne-invert') !== invert) header.toggleAttribute('data-hermyne-invert', invert);
    if (header.dataset.hermyneTone !== tone) header.dataset.hermyneTone = tone;
  }

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      try {
        update();
      } catch {
        header.removeAttribute('data-hermyne-invert');
      }
    });
  };

  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule);
  addEventListener('load', schedule);
  new MutationObserver(schedule).observe(header, { attributes: true, attributeFilter: ['data-sticky-state', 'transparent'] });
  /* slideshows change the photo without any scroll; only worth checking near the top of the page */
  setInterval(() => {
    if (!document.hidden && scrollY < innerHeight) schedule();
  }, 1500);
  schedule();
})();

/*
 * Dropdown menu height.
 * The theme measures a dropdown once, the moment it opens. Its photos load a moment later and make
 * it taller, but the theme keeps the first (too short) height, so only a strip shows and the menu
 * closes as soon as the pointer moves below that strip. This keeps the height in step with the
 * real content while a dropdown is open, using the same two properties the theme sets itself.
 */
(() => {
  const header = document.querySelector('header-component');
  if (!header || !('ResizeObserver' in window)) return;

  const sync = (submenu) => {
    const item = submenu.closest('li');
    if (!item || !item.querySelector(':scope > [aria-expanded="true"]')) return;
    const height = submenu.offsetHeight;
    if (!height) return;
    const overlap = header.hasAttribute('data-submenu-overlap-bottom-row');
    const base = overlap ? header.querySelector('.header__row--top')?.offsetHeight ?? 0 : header.offsetHeight;
    header.style.setProperty('--submenu-height', `${height}px`);
    header.style.setProperty('--full-open-header-height', `${base + height}px`);
  };

  const observer = new ResizeObserver((entries) => entries.forEach((entry) => sync(entry.target)));
  const submenus = header.querySelectorAll('.menu-list__submenu');
  submenus.forEach((submenu) => observer.observe(submenu));

  /* Also re-check right after a dropdown opens and when its photos finish loading */
  header.addEventListener('pointerenter', () => setTimeout(() => submenus.forEach(sync), 60), true);
  header.addEventListener('load', (event) => {
    const submenu = event.target instanceof Element ? event.target.closest('.menu-list__submenu') : null;
    if (submenu) sync(submenu);
  }, true);
})();

/*
 * Dropdown menus stay open while the pointer is anywhere on them.
 * The theme closes a dropdown the instant the pointer leaves the menu item, and the dropdown only
 * counts as "part of the item" once it has finished sliding open, so moving towards it (straight
 * down or diagonally) closed it. Here the close request is held back and only carried out once the
 * pointer is really outside both the header and the open dropdown. Closing itself is still done by
 * the theme's own code.
 */
(() => {
  const header = document.querySelector('header-component');
  if (!header || !window.customElements) return;

  let pointerX = -1;
  let pointerY = -1;
  addEventListener('pointermove', (event) => {
    pointerX = event.clientX;
    pointerY = event.clientY;
  }, { passive: true, capture: true });

  const insideOpenMenu = () => {
    const rect = header.getBoundingClientRect();
    const open = parseFloat(getComputedStyle(header).getPropertyValue('--full-open-header-height')) || 0;
    const bottom = rect.top + Math.max(rect.height, open);
    return pointerY >= rect.top && pointerY <= bottom && pointerX >= 0 && pointerX <= innerWidth;
  };

  customElements.whenDefined('header-menu').then(() => {
    header.querySelectorAll('header-menu').forEach((menu) => {
      if (typeof menu.deactivate !== 'function') return;
      const close = menu.deactivate.bind(menu);
      let timer = 0;
      let pending = null;

      const check = () => {
        if (insideOpenMenu()) {
          timer = setTimeout(check, 120);
          return;
        }
        const event = pending;
        pending = null;
        if (event) close(event);
      };

      menu.deactivate = (event) => {
        if (!event || event.type !== 'pointerleave') return close(event);
        pending = event;
        clearTimeout(timer);
        timer = setTimeout(check, 120);
      };
    });
  });
})();

/* Marks the page while the footer is on screen, so the sticky add-to-cart bar can step aside */
(() => {
  const footer = document.querySelector('.shopify-section-group-footer-group');
  if (!footer || !('IntersectionObserver' in window)) return;
  new IntersectionObserver((entries) => {
    document.body.classList.toggle('hermyne-footer-visible', entries[0].isIntersecting);
  }).observe(footer);
})();

/*
 * Colour thumbnails under a product card: hovering one shows that colour's photo on the card.
 * A preview image is laid over the card's own photo and removed when the pointer leaves the
 * thumbnails, so the card's slideshow and links are not touched.
 */
(() => {
  const large = (src) => {
    try {
      const url = new URL(src, location.href);
      url.searchParams.set('width', '800');
      return url.href;
    } catch {
      return src;
    }
  };

  const show = (swatch) => {
    const card = swatch.closest('product-card');
    const gallery = card?.querySelector('.card-gallery');
    const thumb = swatch.querySelector('img');
    if (!gallery || !thumb) return;
    let preview = gallery.querySelector('.hermyne-swatch-preview');
    if (!preview) {
      preview = document.createElement('img');
      preview.className = 'hermyne-swatch-preview';
      preview.alt = '';
      gallery.appendChild(preview);
    }
    preview.src = large(thumb.currentSrc || thumb.src);
    card.querySelectorAll('.variant-swatch').forEach((el) => el.classList.toggle('is-previewed', el === swatch));
  };

  const hide = (card) => {
    card.querySelector('.hermyne-swatch-preview')?.remove();
    card.querySelectorAll('.variant-swatch.is-previewed').forEach((el) => el.classList.remove('is-previewed'));
  };

  document.addEventListener('pointerover', (event) => {
    const swatch = event.target instanceof Element ? event.target.closest('product-card .variant-swatch') : null;
    if (swatch) show(swatch);
  });

  document.addEventListener('pointerout', (event) => {
    const group = event.target instanceof Element ? event.target.closest('product-card .variant-swatches') : null;
    if (!group || (event.relatedTarget instanceof Node && group.contains(event.relatedTarget))) return;
    const card = group.closest('product-card');
    if (card) hide(card);
  });
})();

/*
 * Product page text rows (description, wash care, policies).
 * Instead of unfolding inside the narrow details column, each row opens its full text in a
 * pop-up in the middle of the screen. The description also shows its first lines with a
 * "Read more" link. The text itself is the theme's own content, copied into the pop-up.
 */
(() => {
  const details = document.querySelector('.product-information .product-details');
  if (!details || typeof HTMLDialogElement === 'undefined') return;

  let dialog = null;
  const getDialog = () => {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'hermyne-info-dialog';
    dialog.innerHTML =
      '<div class="hermyne-info-dialog__head"><h2 class="hermyne-info-dialog__title"></h2>' +
      '<button type="button" class="hermyne-info-dialog__close" aria-label="Close">&#x2715;</button></div>' +
      '<div class="hermyne-info-dialog__body"></div>';
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || (event.target instanceof Element && event.target.closest('.hermyne-info-dialog__close'))) dialog.close();
    });
    document.body.appendChild(dialog);
    return dialog;
  };

  const partsOf = (row) => {
    const summary = row.querySelector('summary');
    const content = row.querySelector('.details-content') || [...(row.querySelector('details')?.children || [])].find((el) => el !== summary);
    return { summary, content };
  };

  const open = (row) => {
    const { summary, content } = partsOf(row);
    if (!summary || !content) return;
    const d = getDialog();
    d.querySelector('.hermyne-info-dialog__title').textContent = summary.textContent.trim();
    const body = d.querySelector('.hermyne-info-dialog__body');
    body.innerHTML = '';
    body.appendChild(content.cloneNode(true));
    body.firstElementChild?.removeAttribute('style');
    body.scrollTop = 0;
    if (!d.open) d.showModal();
  };

  details.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const more = target?.closest('.hermyne-read-more');
    const summary = target?.closest('accordion-custom summary');
    if (!more && !summary) return;
    const row = more ? more.closest('.hermyne-preview')?.previousElementSibling : summary.closest('accordion-custom');
    if (!row) return;
    event.preventDefault();
    event.stopPropagation();
    open(row);
  }, true);

  /* First lines of the description, shown under its heading */
  const first = details.querySelector('accordion-custom');
  if (first) {
    const { content } = partsOf(first);
    const text = (content?.textContent || '').replace(/\s+/g, ' ').replace(/^Product Description\s*/i, '').trim();
    if (text.length > 40) {
      const preview = document.createElement('div');
      preview.className = 'hermyne-preview';
      const p = document.createElement('p');
      p.textContent = text.slice(0, 400);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hermyne-read-more';
      button.textContent = 'Read more';
      preview.append(p, button);
      first.after(preview);
    }
  }
})();

/* Video strip: the tile nearest the middle of the screen is shown a little larger, as on the live site */
(() => {
  const mark = (viewport) => {
    const cells = [...viewport.querySelectorAll('.flickityrf-slider > .carousel-cell')];
    if (!cells.length) return;
    const box = viewport.getBoundingClientRect();
    const middle = box.left + box.width / 2;
    let best = null;
    let bestDistance = Infinity;
    cells.forEach((cell) => {
      const rect = cell.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - middle);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = cell;
      }
    });
    cells.forEach((cell) => cell.classList.toggle('hermyne-centre', cell === best));
  };

  const setup = () => {
    document.querySelectorAll('.reelfy-wrapper .flickityrf-viewport:not([data-hermyne-centre])').forEach((viewport) => {
      viewport.dataset.hermyneCentre = '';
      let queued = false;
      const update = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          mark(viewport);
        });
      };
      viewport.addEventListener('scroll', update, { passive: true });
      addEventListener('resize', update);
      update();
    });
  };

  setup();
  addEventListener('load', setup);
  setTimeout(setup, 2500); /* the app builds its strip a moment after the page loads */
  setTimeout(setup, 6000);
})();

/*
 * Video strip: gentle automatic drift.
 * The strip glides slowly on its own while it is on screen, pauses the moment the pointer or a
 * finger is on it (or the visitor scrolls it), then carries on a little later. At either end it
 * turns round. Visitors who ask their device for reduced motion get no automatic movement.
 */
(() => {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const SPEED = 28; /* pixels per second */

  const start = (viewport) => {
    if (viewport.dataset.hermyneDrift !== undefined) return;
    viewport.dataset.hermyneDrift = '';
    let direction = 1;
    let visible = false;
    let pausedUntil = 0;
    let hovering = false;
    let last = 0;
    let position = viewport.scrollLeft;
    let selfScroll = false;

    const pause = (ms) => {
      pausedUntil = performance.now() + ms;
    };

    viewport.addEventListener('pointerenter', () => (hovering = true));
    viewport.addEventListener('pointerleave', () => {
      hovering = false;
      pause(900);
    });
    viewport.addEventListener('touchstart', () => pause(4000), { passive: true });
    viewport.addEventListener('wheel', () => pause(2500), { passive: true });
    viewport.addEventListener('scroll', () => {
      if (!selfScroll) position = viewport.scrollLeft;
      selfScroll = false;
    }, { passive: true });

    new IntersectionObserver((entries) => (visible = entries[0].isIntersecting), { threshold: 0.25 }).observe(viewport);

    const frame = (now) => {
      const delta = Math.min(64, now - (last || now));
      last = now;
      if (visible && !hovering && !document.hidden && now > pausedUntil) {
        const max = viewport.scrollWidth - viewport.clientWidth;
        if (max > 4) {
          position += direction * SPEED * (delta / 1000);
          if (position >= max) {
            position = max;
            direction = -1;
            pause(1200);
          } else if (position <= 0) {
            position = 0;
            direction = 1;
            pause(1200);
          }
          selfScroll = true;
          viewport.scrollLeft = position;
        }
      } else {
        position = viewport.scrollLeft;
      }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  };

  const setup = () => document.querySelectorAll('.reelfy-wrapper .flickityrf-viewport').forEach(start);
  setup();
  addEventListener('load', setup);
  setTimeout(setup, 2500);
  setTimeout(setup, 6000);
})();

/*
 * Quick add opens fast.
 * The theme only starts downloading a product's details after "+ Add" is clicked, so the pop-up
 * waited on the network. Here the download starts as soon as the pointer (or a finger) reaches
 * the product card, and each product is only ever downloaded once per page. The pop-up itself is
 * still built by the theme's own code.
 */
(() => {
  if (!window.customElements) return;
  const pages = new Map();
  window.hermyneQuickAddPages = pages;

  const load = (url) => {
    if (!url) return Promise.resolve(null);
    if (!pages.has(url)) {
      const request = fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
        .then((text) => new DOMParser().parseFromString(text, 'text/html'))
        .catch(() => {
          pages.delete(url);
          return null;
        });
      pages.set(url, request);
    }
    return pages.get(url);
  };

  const adopt = (component) => {
    if (component.hermyneFast) return;
    component.hermyneFast = true;
    component.fetchProductPage = (url) => load(url);
  };

  const warm = (event) => {
    const card = event.target instanceof Element ? event.target.closest('product-card') : null;
    const component = card?.querySelector('quick-add-component');
    if (!component || component.hermyneWarmed) return;
    /* cards that add straight from a size never open the pop-up: do not fetch it */
    if (card.querySelector('.hermyne-sizes') && window.matchMedia('(hover: hover)').matches) return;
    component.hermyneWarmed = true;
    adopt(component);
    try {
      load(component.productPageUrl);
    } catch {
      /* the theme will fetch on click as usual */
    }
  };

  customElements.whenDefined('quick-add-component').then(() => {
    document.querySelectorAll('quick-add-component').forEach(adopt);
    document.addEventListener('pointerover', warm, { passive: true });
    document.addEventListener('touchstart', warm, { passive: true });
    document.addEventListener('focusin', warm);
  });
})();

/*
 * "+ Add" responds at once.
 * If the product's details are still downloading when "+ Add" is clicked, nothing used to happen
 * on screen until they arrived, which felt like the click was ignored. Now the bouncing sunflower
 * shows straight away over a dimmed page and goes as soon as the pop-up (or the cart) opens.
 */
(() => {
  const loader = document.getElementById('hermyne-loader');
  if (!loader) return;
  let timer = 0;
  let watch = 0;

  const done = () => {
    clearTimeout(timer);
    clearInterval(watch);
    loader.classList.remove('is-showing', 'is-light');
    loader.classList.add('is-hidden');
  };

  const somethingOpened = () =>
    [...document.querySelectorAll('dialog')].some((dialog) => dialog.open && !dialog.classList.contains('hermyne-info-dialog'));

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('product-card .quick-add__button') : null;
    if (!button) return;
    clearTimeout(timer);
    clearInterval(watch);
    /* only if it is not instant */
    timer = setTimeout(() => {
      if (somethingOpened()) return;
      loader.classList.remove('is-hidden');
      loader.classList.add('is-showing', 'is-light');
      watch = setInterval(() => {
        if (somethingOpened()) done();
      }, 60);
      timer = setTimeout(done, 8000);
    }, 150);
  }, true);
})();

/*
 * Size chart inside the quick-add pop-up.
 * The theme leaves the size chart out of the pop-up. A "Size chart" link is added on the same
 * line as the Size label; it opens the product's own size chart (taken from the product page that
 * was already downloaded for the pop-up) in a small centred panel.
 */
(() => {
  let lastUrl = null;
  let panel = null;

  const getPanel = () => {
    if (panel) return panel;
    panel = document.createElement('dialog');
    panel.className = 'hermyne-info-dialog';
    panel.innerHTML =
      '<div class="hermyne-info-dialog__head"><h2 class="hermyne-info-dialog__title">Size chart</h2>' +
      '<button type="button" class="hermyne-info-dialog__close" aria-label="Close">&#x2715;</button></div>' +
      '<div class="hermyne-info-dialog__body"></div>';
    panel.addEventListener('click', (event) => {
      if (event.target === panel || (event.target instanceof Element && event.target.closest('.hermyne-info-dialog__close'))) panel.close();
    });
    document.body.appendChild(panel);
    return panel;
  };

  const chartFor = async (url) => {
    const pages = window.hermyneQuickAddPages;
    const doc = pages && pages.has(url) ? await pages.get(url) : null;
    return doc ? doc.querySelector('[class*="wsp-size-chart-content-"]') : null;
  };

  const addLink = async (modal, url) => {
    if (modal.querySelector('.hermyne-size-chart-link')) return;
    const legend = modal.querySelector('variant-picker legend');
    if (!legend) return;
    const chart = await chartFor(url);
    if (!chart || modal.querySelector('.hermyne-size-chart-link')) return;
    const link = document.createElement('button');
    link.type = 'button';
    link.className = 'hermyne-size-chart-link';
    link.textContent = 'Size chart';
    link.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      const content = await chartFor(url);
      if (!content) return;
      const p = getPanel();
      const body = p.querySelector('.hermyne-info-dialog__body');
      body.innerHTML = '';
      const copy = content.cloneNode(true);
      copy.querySelectorAll('img').forEach((img) => {
        img.removeAttribute('loading');
        img.style.cssText = 'width:100%;height:auto;border-radius:12px';
      });
      body.appendChild(copy);
      if (!p.open) p.showModal();
    });
    legend.classList.add('hermyne-size-row');
    legend.appendChild(link);
  };

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('product-card .quick-add__button') : null;
    const component = button?.closest('quick-add-component');
    if (!component) return;
    try {
      lastUrl = component.productPageUrl;
    } catch {
      lastUrl = null;
    }
    let tries = 0;
    const wait = setInterval(() => {
      const modal = document.querySelector('dialog.quick-add-modal');
      if (modal && modal.open && lastUrl) {
        addLink(modal, lastUrl);
        if (!modal.hermyneSizeChartWatch) {
          /* the theme redraws the size picker when a size is picked; put the link back when it does */
          modal.hermyneSizeChartWatch = new MutationObserver(() => {
            if (modal.open && lastUrl && !modal.querySelector('.hermyne-size-chart-link')) addLink(modal, lastUrl);
          });
          modal.hermyneSizeChartWatch.observe(modal, { childList: true, subtree: true });
        }
        if (modal.querySelector('.hermyne-size-chart-link') || tries > 60) clearInterval(wait);
      }
      if (++tries > 120) clearInterval(wait);
    }, 100);
  }, true);
})();

/*
 * One spelling for "Co-ord Set" in product names wherever they are shown.
 * Product names in the store spell it several ways (coord set, coord-set, Co-Ord set...). The
 * store's data is left alone; the name is tidied on screen only.
 */
(() => {
  const SELECTOR = [
    'product-card [class*="product_title"]',
    'product-card .product-card__link .visually-hidden',
    '.product-information .product-details h1',
    '.quick-add-modal :is(h1, h2, .product-header a)',
    '.sticky-add-to-cart__title',
    '.cart-items__title, .cart-items [class*="title"] a',
    '.hermyne-sale__name',
    '.reelfy_card [class*="title"], .reelfy_card [class*="name"]',
    '.jdgm-carousel-item [class*="product"]',
    '.resource-card__title',
    '.menu-list__link-title, .mega-menu__link .wrap-text, .menu-drawer__menu-item-text',
    '.collection-card__content :is(h2, h3, h4, p)',
    'main .text-block.h2 :is(h1, h2)',
  ].join(',');
  const PATTERN = /\bco[\s-]?ord(?:inate)?[\s-]*(sets?)\b/gi;
  const fix = (text) =>
    text
      .replace(/\bRaksha\s+Bandhan(\s+Collection)?\b/gi, 'Festive')
      .replace(PATTERN, (match, word) => 'Co-ord ' + (word.toLowerCase() === 'sets' ? 'Sets' : 'Set'));

  const tidy = (root) => {
    if (!(root instanceof Element || root instanceof Document)) return;
    const targets = root instanceof Element && root.matches(SELECTOR) ? [root] : [];
    root.querySelectorAll(SELECTOR).forEach((el) => targets.push(el));
    targets.forEach((el) => {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        const next = fix(node.nodeValue);
        if (next !== node.nodeValue) node.nodeValue = next;
      }
    });
  };

  tidy(document);
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      tidy(document);
    }, 200);
  }).observe(document.body, { childList: true, subtree: true });
})();

/*
 * Header pieces step aside for what scrolls under them.
 * The header is see-through, so when a title, a label on a photo or a button scrolls under the
 * menu links, the logo or the icons, the two overlap and neither can be read. While something is
 * passing under one part of the header (left group, logo, right group), that part fades out, and
 * it comes back as soon as the thing has gone. Works the same on phones, where the header's
 * columns are not boxes of their own, so each part is measured from what is actually drawn.
 */
(() => {
  const header = document.querySelector('header-component');
  if (!header) return;
  const parts = [
    { column: '.header__column--left', cls: 'hermyne-links-hidden' },
    /* the brand name never fades on phones: it is the one thing in the bar that says whose shop this is */
    { column: '.header__column--center', cls: 'hermyne-logo-hidden', only: '.header-logo__image', notOnPhones: true },
    { column: '.header__column--right', cls: 'hermyne-icons-hidden' },
  ];
  /* titles, labels on photos and the buttons that sit with them; product names in cards are left out */
  const THINGS = [
    'main :is(h1, h2, h3)',
    'main [class*="product_list_text"]',
    'main .text-block.h2',
    'main a.link[class*="product_list_button"]',
    'main .slide__content :is(.button, a.button)',
    '.hermyne-tiles__label',
    'main .collection-card__content :is(h2, h3, h4, p, span)',
    '.hermyne-collection-head__count',
    '.hermyne-sort-by__button',
    '.hermyne-styles__chip',
    '.hermyne-breadcrumb',
    '.hermyne-offer__big, .hermyne-offer__sub, .hermyne-offer__code',
    '.hermyne-blog__heading',
    '.jdgm-cards-carousel .jdgm-title',
    'main .products-count-wrapper',
  ].join(', ');
  const SKIP = 'product-card, .product-card, .hermyne-menu, dialog, [hidden]';
  const phone = window.matchMedia('(max-width: 749px)');
  let queued = false;

  /* the box a header part really occupies: the union of its visible pieces */
  const boxOf = (part) => {
    const column = header.querySelector(part.column);
    if (!column) return null;
    const pieces = part.only
      ? [...column.querySelectorAll(part.only)]
      : [...column.querySelectorAll('summary, button, a, .menu-list__link')];
    const bar = header.getBoundingClientRect();
    let box = null;
    for (const piece of pieces) {
      const rect = piece.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) continue;
      /* the closed menu drawer and its links live in the same column, off screen: not part of the bar */
      if (rect.right <= 0 || rect.left >= window.innerWidth) continue;
      if (rect.top < bar.top - 8 || rect.bottom > bar.bottom + 8) continue;
      if (!box) box = { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom };
      else {
        box.left = Math.min(box.left, rect.left);
        box.right = Math.max(box.right, rect.right);
        box.top = Math.min(box.top, rect.top);
        box.bottom = Math.max(box.bottom, rect.bottom);
      }
    }
    return box;
  };

  const check = () => {
    queued = false;
    if (header.querySelector('.menu-list__link[aria-expanded="true"]')) return;
    if (document.body.classList.contains('hermyne-menu-open')) return;
    const boxes = parts.map((part) => (part.notOnPhones && phone.matches ? null : boxOf(part)));
    const covered = parts.map(() => false);
    const live = boxes.filter(Boolean);
    if (live.length) {
      const top = Math.min(...live.map((b) => b.top)) - 4;
      const bottom = Math.max(...live.map((b) => b.bottom)) + 4;
      for (const thing of document.querySelectorAll(THINGS)) {
        const rect = thing.getBoundingClientRect();
        if (rect.width < 8 || rect.height < 8 || rect.bottom < top || rect.top > bottom) continue;
        if (thing.closest(SKIP)) continue;
        /* the words themselves, not the full-width block that holds them */
        const range = document.createRange();
        range.selectNodeContents(thing);
        const text = range.getBoundingClientRect();
        const area = text.width ? text : rect;
        boxes.forEach((box, index) => {
          if (!box || covered[index]) return;
          if (area.right > box.left - 4 && area.left < box.right + 4 && area.bottom > box.top && area.top < box.bottom) {
            covered[index] = true;
          }
        });
        if (covered.every(Boolean)) break;
      }
    }
    parts.forEach((part, index) => {
      if (header.classList.contains(part.cls) !== covered[index]) header.classList.toggle(part.cls, covered[index]);
    });
  };

  /* once per frame; the timer covers browsers that hold frames back (low-power mode, background tabs) */
  const schedule = () => {
    if (queued) return;
    queued = true;
    let ran = false;
    const run = () => {
      if (ran) return;
      ran = true;
      check();
    };
    requestAnimationFrame(run);
    setTimeout(run, 200);
  };

  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule);
  schedule();
})();

/*
 * Reviews: "Read more".
 * Review cards are trimmed to keep the row tidy. A card whose text was cut gets a "Read more"
 * link that opens the full review (and the store's reply) in the same centred panel the product
 * description uses. The reviews themselves come from the reviews app and are not changed.
 */
(() => {
  if (typeof HTMLDialogElement === 'undefined') return;
  let panel = null;

  const getPanel = () => {
    if (panel) return panel;
    panel = document.createElement('dialog');
    panel.className = 'hermyne-info-dialog hermyne-review-dialog';
    panel.innerHTML =
      '<div class="hermyne-info-dialog__head"><h2 class="hermyne-info-dialog__title"></h2>' +
      '<button type="button" class="hermyne-info-dialog__close" aria-label="Close">&#x2715;</button></div>' +
      '<div class="hermyne-info-dialog__body"></div>';
    panel.addEventListener('click', (event) => {
      if (event.target === panel || (event.target instanceof Element && event.target.closest('.hermyne-info-dialog__close'))) panel.close();
    });
    document.body.appendChild(panel);
    return panel;
  };

  const open = (card) => {
    const p = getPanel();
    const author = card.querySelector('.jdgm-rev__author')?.textContent.trim() || 'Review';
    p.querySelector('.hermyne-info-dialog__title').textContent = author;
    const body = p.querySelector('.hermyne-info-dialog__body');
    body.innerHTML = '';
    const copy = card.cloneNode(true);
    copy.className = 'hermyne-review-full';
    copy.removeAttribute('style');
    copy.querySelectorAll('.hermyne-read-more, .jdgm-rev__author, .jdgm-rev__icon, .jdgm-rev__actions, .jdgm-rev__br').forEach((el) => el.remove());
    body.appendChild(copy);
    body.scrollTop = 0;
    if (!p.open) p.showModal();
  };

  const cut = (el) => el && el.scrollHeight > el.clientHeight + 2;

  const mark = () => {
    document.querySelectorAll('.jdgm-rev-widg .jdgm-rev').forEach((card) => {
      if (card.querySelector('.hermyne-read-more')) return;
      const parts = [...card.querySelectorAll('.jdgm-rev__body, .jdgm-rev__body p, .jdgm-rev__reply-content, .jdgm-rev__reply p')];
      if (!parts.some(cut) && card.scrollHeight <= card.clientHeight + 2) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hermyne-read-more hermyne-read-more--review';
      button.textContent = 'Read more';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        open(card);
      });
      card.appendChild(button);
    });
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      mark();
    }, 300);
  };

  const start = () => {
    const widget = document.querySelector('.jdgm-rev-widg');
    if (!widget) return false;
    new MutationObserver(schedule).observe(widget, { childList: true, subtree: true });
    schedule();
    return true;
  };

  if (!start()) {
    let tries = 0;
    const wait = setInterval(() => {
      if (start() || ++tries > 40) clearInterval(wait);
    }, 500);
  }
  addEventListener('load', schedule);
})();

/*
 * Reviews: one continuous sideways scroll, no page numbers.
 * The reviews app shows five reviews per page. Here the page numbers are hidden and, when the row
 * is scrolled near its end, the app's own "next page" is triggered and the new reviews are added
 * to the end of the row, keeping the ones already shown.
 */
(() => {
  let kept = [];
  let loading = false;
  let finished = false;
  let heldY = null;

  const setup = (widget) => {
    const row = () => widget.querySelector('.jdgm-rev-widg__reviews');
    const idOf = (card) => card.getAttribute('data-review-id') || card.textContent.trim().slice(0, 80);
    const nextLink = () => widget.querySelector('.jdgm-paginate__next-page');

    const loadMore = () => {
      const list = row();
      const next = nextLink();
      if (!list || loading || finished) return;
      if (!next) {
        finished = true;
        return;
      }
      kept = [...list.querySelectorAll('.jdgm-rev')].map((card) => ({ id: idOf(card), node: card }));
      loading = true;
      list.dataset.hermyneScroll = String(list.scrollLeft);
      const pageY = scrollY;
      heldY = pageY;
      next.click();
      /* The app animates the page up to the top of the reviews when it changes page. While the
         extra reviews load, any page scroll is put straight back, and the app's own scroll
         animation is stopped, so the visitor stays where they are. */
      const pin = () => {
        if (Math.abs(scrollY - pageY) > 1) {
          document.documentElement.scrollTop = pageY;
          document.body.scrollTop = pageY;
        }
      };
      const stopAppScroll = () => {
        try {
          const jq = (window.jdgm && window.jdgm.$) || window.jQuery;
          if (jq) jq('html, body').stop(true, false);
        } catch (e) {}
      };
      window.addEventListener('scroll', pin, true);
      let n = 0;
      const hold = setInterval(() => {
        stopAppScroll();
        pin();
        if (++n > 100 || (!loading && n > 30)) {
          clearInterval(hold);
          window.removeEventListener('scroll', pin, true);
        }
      }, 40);
      setTimeout(() => (loading = false), 6000);
    };

    const merge = () => {
      const list = row();
      if (!list || !loading) return;
      const fresh = [...list.querySelectorAll('.jdgm-rev')];
      if (!fresh.length) return;
      const freshIds = new Set(fresh.map(idOf));
      if (kept.length && kept.every((item) => freshIds.has(item.id))) return; /* nothing new yet */
      const missing = kept.filter((item) => !freshIds.has(item.id));
      if (!missing.length) return;
      const first = list.firstChild;
      missing.forEach((item) => list.insertBefore(item.node, first));
      list.scrollLeft = Number(list.dataset.hermyneScroll || 0);
      loading = false;
      if (!nextLink()) finished = true;
    };

    const onScroll = () => {
      const list = row();
      if (list && list.scrollLeft + list.clientWidth > list.scrollWidth - 500) loadMore();
    };

    widget.addEventListener('scroll', onScroll, { passive: true, capture: true });
    /* a new sort order starts the row again from its first five reviews */
    widget.addEventListener('change', (event) => {
      if (!(event.target instanceof Element) || !event.target.matches('.jdgm-sort-dropdown')) return;
      kept = [];
      loading = false;
      finished = false;
      const list = row();
      if (list) list.scrollLeft = 0;
      /* keep the page where it is while the app redraws the reviews (it scrolls up otherwise) */
      const sortY = scrollY;
      const pinSort = () => {
        if (Math.abs(scrollY - sortY) > 1) {
          document.documentElement.scrollTop = sortY;
          document.body.scrollTop = sortY;
        }
      };
      window.addEventListener('scroll', pinSort, true);
      let m = 0;
      const holdSort = setInterval(() => {
        try {
          const jq = (window.jdgm && window.jdgm.$) || window.jQuery;
          if (jq) jq('html, body').stop(true, false);
        } catch (e) {}
        pinSort();
        if (++m > 60) {
          clearInterval(holdSort);
          window.removeEventListener('scroll', pinSort, true);
        }
      }, 40);
      setTimeout(onScroll, 2500);
    }, true);
    new MutationObserver(() => setTimeout(merge, 60)).observe(widget, { childList: true, subtree: true });
    /* if the first reviews do not fill the row, fetch more straight away */
    setTimeout(onScroll, 1500);
  };

  let tries = 0;
  const wait = setInterval(() => {
    const widget = document.querySelector('.jdgm-rev-widg');
    if (widget && widget.querySelector('.jdgm-rev')) {
      clearInterval(wait);
      setup(widget);
    } else if (++tries > 40) clearInterval(wait);
  }, 500);
})();

/*
 * Reviews: a proper sort menu.
 * The reviews app's sort control is a plain system drop-down. It is kept (and still does the
 * sorting) but hidden behind a small button with a rounded menu that matches the site.
 */
(() => {
  const build = (select) => {
    if (select.hermyneMenu) return;
    if (!select.offsetParent && !select.classList.contains('hermyne-sort__native')) return; /* a hidden spare copy */
    select.hermyneMenu = true;
    const wrap = document.createElement('div');
    wrap.className = 'hermyne-sort';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hermyne-sort__button';
    button.setAttribute('aria-haspopup', 'listbox');
    const menu = document.createElement('ul');
    menu.className = 'hermyne-sort__menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;

    const label = (text) => text.trim().toLowerCase().replace(/^./, (c) => c.toUpperCase());
    const refresh = () => {
      const current = select.options[select.selectedIndex];
      button.textContent = 'Sort: ' + (current ? label(current.textContent) : '');
      [...menu.children].forEach((li) => li.classList.toggle('is-current', li.dataset.value === select.value));
    };

    [...select.options].forEach((option) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.dataset.value = option.value;
      li.textContent = label(option.textContent);
      li.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        menu.hidden = true;
        refresh();
      });
      menu.appendChild(li);
    });

    button.addEventListener('click', (event) => {
      event.stopPropagation();
      menu.hidden = !menu.hidden;
    });
    document.addEventListener('click', (event) => {
      if (!wrap.contains(event.target)) menu.hidden = true;
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') menu.hidden = true;
    });

    wrap.append(button, menu);
    select.after(wrap);
    select.classList.add('hermyne-sort__native');
    refresh();
  };

  let tries = 0;
  const wait = setInterval(() => {
    const selects = [...document.querySelectorAll('.jdgm-rev-widg .jdgm-sort-dropdown')];
    if (selects.length) {
      selects.forEach(build);
      const widget = selects[0].closest('.jdgm-rev-widg');
      new MutationObserver(() => {
        widget.querySelectorAll('.jdgm-sort-dropdown').forEach(build);
      }).observe(widget, { childList: true, subtree: true });
      clearInterval(wait);
    } else if (++tries > 40) clearInterval(wait);
  }, 500);
})();

/*
 * Hero slideshow keeps moving.
 * The theme stops the hero's automatic slides whenever the pointer is over it, and the hero fills
 * the screen, so on a laptop it almost never advanced. This fills that gap: while the pointer is
 * over the hero it moves to the next slide on the same timing. The pause button still pauses it,
 * and it does nothing while the tab is in the background or the hero is off screen.
 */
(() => {
  const start = (component) => {
    if (component.hermyneAutoplay) return;
    const seconds = parseInt(component.getAttribute('autoplay') || '', 10);
    if (!seconds || typeof component.next !== 'function') return;
    component.hermyneAutoplay = true;
    let lastTouch = 0;
    ['click', 'keydown', 'touchstart', 'wheel'].forEach((type) =>
      component.addEventListener(type, () => (lastTouch = Date.now()), { passive: true, capture: true })
    );
    setInterval(() => {
      if (document.hidden || component.hasAttribute('paused') && !component.matches(':hover')) return;
      if (!component.matches(':hover')) return; /* the theme's own timer handles this case */
      if (component.dataset.hermyneUserPaused === 'true') return;
      if (Date.now() - lastTouch < seconds * 1000) return; /* the visitor just changed slide themselves */
      const rect = component.getBoundingClientRect();
      if (rect.bottom < 120 || rect.top > innerHeight - 120) return;
      component.next();
    }, seconds * 1000);

    /* remember a deliberate press of the pause button */
    component.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('.icon-pause, .icon-play, [class*="slideshow-control--pause"], [aria-label*="Pause"], [aria-label*="Play"]') : null;
      if (!button) return;
      const now = component.dataset.hermyneUserPaused === 'true';
      component.dataset.hermyneUserPaused = String(!now);
    }, true);
  };

  const setup = () =>
    document.querySelectorAll('[id$="__slideshow_rAJRDE"] slideshow-component, [id$="__slideshow_iG7t88"] slideshow-component').forEach(start);

  if (window.customElements) customElements.whenDefined('slideshow-component').then(setup);
  addEventListener('load', setup);
})();

/* Back-to-top button: appears on the right after scrolling down a screen, sits above the chat button */
(() => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'hermyne-to-top';
  button.setAttribute('aria-label', 'Back to top');
  button.innerHTML =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6"/></svg>';
  button.addEventListener('click', () => {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  });
  document.body.appendChild(button);

  let queued = false;
  const update = () => {
    queued = false;
    button.classList.toggle('is-visible', scrollY > innerHeight * 0.8);
  };
  addEventListener('scroll', () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  }, { passive: true });
  update();
})();

/*
 * Sizes on product cards.
 * Clicking a size on a card adds that size to the cart through Shopify's standard cart request,
 * marks the size with a dot, and tells the theme the cart changed so it refreshes and opens the
 * cart drawer exactly as it does after a normal "Add to cart".
 */
(() => {
  /* the sizes sit on the product photo: move each row into its card's photo area */
  const place = () => {
    document.querySelectorAll('product-card .hermyne-sizes:not([data-placed])').forEach((row) => {
      const gallery = row.closest('product-card')?.querySelector('.card-gallery');
      if (!gallery) return;
      row.dataset.placed = '';
      gallery.appendChild(row);
    });
  };
  place();
  let placing = false;
  new MutationObserver(() => {
    if (placing) return;
    placing = true;
    setTimeout(() => {
      placing = false;
      place();
    }, 150);
  }).observe(document.body, { childList: true, subtree: true });

  const openCart = () => {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer?.querySelector('dialog')?.open) return;
    try {
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
    } catch (e) {}
  };

  /*
   * A waiting cover for a pop-up: the bouncing sunflower and "Adding to your cart" drawn inside
   * the cart (or inside the quick-add sheet on phones) while the add is on its way.
   * Returns a function that takes it away again.
   */
  const cover = (dialog, heading) => {
    if (!dialog) return () => {};
    dialog.querySelectorAll(':scope > .hermyne-cart-wait').forEach((old) => old.remove());
    const box = document.createElement('div');
    box.className = 'hermyne-cart-wait';
    box.setAttribute('role', 'status');
    if (heading) {
      const title = document.createElement('h2');
      title.className = 'h4';
      title.textContent = heading;
      box.appendChild(title);
    }
    const middle = document.createElement('div');
    middle.className = 'hermyne-cart-wait__middle';
    const flower = document.querySelector('#hermyne-loader .hermyne-loader__flower');
    if (flower) middle.appendChild(flower.cloneNode(true));
    const label = document.createElement('span');
    label.textContent = 'Adding to your cart';
    middle.appendChild(label);
    box.appendChild(middle);
    dialog.appendChild(box);
    return () => box.remove();
  };

  /* After an add the theme redraws the drawer, which can close it a moment later (it does when
     the cart was empty). Keep bringing it back until it has stayed open with the item in it. */
  const keepOpen = () => {
    let steady = 0;
    let tries = 0;
    let closed = 0;
    const timer = setInterval(() => {
      const dialog = document.querySelector('cart-drawer-component dialog');
      const ready = dialog?.open && dialog.querySelector('.cart-items__table-row');
      if (!dialog?.open) {
        closed += 1;
        openCart();
        /* the theme opens on the next frame; if that keeps not happening, open it directly */
        if (closed >= 3) {
          try {
            dialog.showModal();
          } catch (e) {}
        }
      } else {
        closed = 0;
      }
      steady = ready ? steady + 1 : 0;
      if (steady >= 4 || ++tries > 24) clearInterval(timer);
    }, 180);
  };

  const cartSections = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);

  /* a small note at the bottom of the screen while the add is on its way */
  const toast = (text, state) => {
    let note = document.querySelector('.hermyne-toast');
    if (!note) {
      note = document.createElement('div');
      note.className = 'hermyne-toast';
      note.setAttribute('role', 'status');
      document.body.appendChild(note);
    }
    note.textContent = text;
    note.dataset.state = state || '';
    note.hidden = false;
    clearTimeout(note.timer);
    return () => {
      note.timer = setTimeout(() => (note.hidden = true), state === 'error' ? 3200 : 150);
    };
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /*
   * The cart opens on the click and shows the sunflower while the add is on its way; the finished
   * cart then appears in the same open drawer. This is safe because the cart dialog carries
   * data-skip-node-update (snippets/header-actions.liquid): when the add arrives and the theme
   * redraws the header, the dialog's own "open" state is left alone and only what is inside it is
   * redrawn. (Without that marker the redraw closed an open cart and left the page locked.)
   */
  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest('.hermyne-sizes__size') : null;
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    if (button.disabled || button.classList.contains('is-loading')) return;
    const group = button.closest('.hermyne-sizes');
    group.querySelectorAll('.is-picked, .is-error').forEach((el) => el.classList.remove('is-picked', 'is-error'));
    button.classList.add('is-picked', 'is-loading');
    button.blur();
    let done = () => {};
    openCart();
    await wait(60);
    let dialog = document.querySelector('cart-drawer-component dialog');
    const marked = !!dialog && dialog.hasAttribute('data-skip-node-update');
    /* with the marker the cart can wait open; without it (older page still on screen) keep the old order */
    let lift = marked ? cover(dialog, 'Cart') : () => {};
    if (!marked) done = toast('Adding to your cart…');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const sections = cartSections();
      const response = await fetch(root + 'cart/add.js', {
        signal: controller.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error('add failed');
      const data = await response.json();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'hermyne-sizes',
            /* this source makes the header count go up by one instead of being set to one */
            data: { source: 'product-form-component', itemCount: 1, variantId: button.dataset.variantId, sections: data.sections || undefined },
          },
        })
      );
      /* let the theme finish redrawing what is inside the cart, then lift the cover */
      await wait(marked ? 320 : 250);
      lift();
      openCart();
      keepOpen();
      /* the header's little count is set from the cart itself, so it can never drift */
      fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then((reply) => reply.json())
        .then((cart) => {
          const icon = document.querySelector('cart-icon');
          if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count, false, false);
          sessionStorage.setItem('cart-count', JSON.stringify({ value: String(cart.item_count), timestamp: Date.now() }));
        })
        .catch(() => {});
    } catch (error) {
      lift();
      button.classList.remove('is-picked');
      button.classList.add('is-error');
      done = toast('That did not go through. Please try again.', 'error');
    } finally {
      clearTimeout(timeout);
      button.classList.remove('is-loading');
      done();
      setTimeout(() => button.classList.remove('is-picked', 'is-error'), 1800);
    }
  }, true);

  /*
   * Quick-add sheet (the pop-up phones use): while its own "Add to cart" is working, the
   * sunflower shows inside the sheet. It is lifted when the cart answers, on an error, or after
   * a few seconds whatever happens. The theme then closes the sheet and opens the cart as usual.
   */
  let liftSheet = null;
  const clearSheet = () => {
    if (liftSheet) liftSheet();
    liftSheet = null;
  };
  document.addEventListener('submit', (event) => {
    const form = event.target instanceof Element ? event.target : null;
    const sheet = form?.closest('dialog.quick-add-modal');
    if (!sheet || !sheet.open) return;
    const submit = form.querySelector('button[type="submit"], button[name="add"]');
    if (submit && (submit.disabled || submit.getAttribute('aria-disabled') === 'true')) return;
    clearSheet();
    liftSheet = cover(sheet, '');
    setTimeout(clearSheet, 7000);
  }, true);
  document.addEventListener('cart:update', () => setTimeout(clearSheet, 150));
  document.addEventListener('cart:error', clearSheet);
})();

/*
 * Product page trail: remember the collection the shopper was browsing, and point the trail on
 * the product page back to it (only when the product really is in that collection).
 */
(() => {
  const KEY = 'hermyne-last-collection';
  const match = location.pathname.match(/\/collections\/([^/?#]+)\/?$/);
  try {
    if (match && match[1] !== 'all') {
      const heading = document.querySelector('main h1, .main-collection-title, h1');
      const title = (heading?.textContent || document.title.split(' – ')[0] || '').trim();
      if (title) sessionStorage.setItem(KEY, JSON.stringify({ handle: match[1], title, url: location.pathname + location.search }));
    }
    const trail = document.querySelector('.hermyne-breadcrumb');
    if (!trail) return;
    const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (!saved || !(trail.dataset.collections || '').split(',').includes(saved.handle)) return;
    const link = trail.querySelector('.hermyne-breadcrumb__collection');
    const names = { 'raksha-bandhan-collection': 'Festive' };
    /* only ever a path on this store, never a full address from storage */
    if (typeof saved.url !== 'string' || !/^\/(?!\/)/.test(saved.url)) return;
    link.href = saved.url;
    link.textContent = names[saved.handle] || saved.title;
  } catch {
    /* the trail keeps the collection it was drawn with */
  }
})();

/*
 * Cart drawer: keep the "empty" marker honest.
 * The theme sets it when a page loads with an empty cart and styles the drawer centred for it,
 * but does not always clear it when an item is added without a page load, which left a filled
 * cart laid out like the empty one. It now follows what is actually in the cart.
 */
(() => {
  const sync = () => {
    const dialog = document.querySelector('cart-drawer-component dialog');
    if (!dialog) return;
    const filled = !!dialog.querySelector('.cart-items__table-row');
    dialog.classList.toggle('cart-drawer--empty', !filled);
  };
  const start = () => {
    const holder = document.querySelector('cart-drawer-component dialog');
    if (!holder) return;
    sync();
    new MutationObserver(sync).observe(holder, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/*
 * Back to the same spot on the homepage.
 * The homepage remembers how far down the shopper was when they left it. Coming back with the
 * "Home" link in the trail (or the browser's back button) returns them to that spot instead of
 * the top of the page.
 */
(() => {
  const SPOT = 'hermyne-home-spot';
  const RETURN = 'hermyne-home-return';
  const isHome = document.querySelector('main')?.dataset.template === 'index';
  try {
    /*
     * Which way back the trail offers depends on how the shopper got here:
     *  - opened from something on the homepage itself -> "Home", back to the same spot;
     *  - opened from the menu, a category page, search or a link from outside -> the category.
     * The homepage notes whether the last touch was on its own content or on the menu, and the
     * next page reads that together with where the browser says it came from.
     */
    const VIA = 'hermyne-left-home-via';
    const PAGES = 'hermyne-from-home-pages';
    const pages = () => {
      try {
        return JSON.parse(sessionStorage.getItem(PAGES) || '[]');
      } catch {
        return [];
      }
    };

    if (isHome) {
      const note = (event) => {
        if (!(event.target instanceof Element)) return;
        const inMenu = event.target.closest('header, header-component, #header-group, .menu-drawer, .hermyne-menu, footer, dialog');
        sessionStorage.setItem(VIA, inMenu ? 'menu' : 'page');
      };
      document.addEventListener('pointerdown', note, true);
      document.addEventListener('click', note, true);
    } else {
      let cameFromHome = false;
      try {
        const from = document.referrer ? new URL(document.referrer) : null;
        cameFromHome = !!from && from.origin === location.origin && from.pathname.replace(/\/+$/, '') === '';
      } catch {
        cameFromHome = false;
      }
      const here = location.pathname;
      let list = pages();
      if (cameFromHome) {
        list = list.filter((path) => path !== here);
        if (sessionStorage.getItem(VIA) === 'page') list.push(here);
        sessionStorage.setItem(PAGES, JSON.stringify(list.slice(-30)));
      }
      if (list.includes(here)) document.documentElement.classList.add('hermyne-from-home');

      document.addEventListener('click', (event) => {
        const home = event.target instanceof Element ? event.target.closest('.hermyne-breadcrumb__home') : null;
        if (home) sessionStorage.setItem(RETURN, '1');
      }, true);
      return;
    }

    const save = () => sessionStorage.setItem(SPOT, String(Math.round(window.scrollY)));
    const back = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
    const asked = sessionStorage.getItem(RETURN) === '1';
    sessionStorage.removeItem(RETURN);
    const spot = Number(sessionStorage.getItem(SPOT) || 0);

    if ((asked || back) && spot > 0) {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      let stopped = false;
      const stop = () => (stopped = true);
      ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((type) => window.addEventListener(type, stop, { once: true, passive: true }));
      const started = Date.now();
      /* the page grows as photos arrive: keep stepping to the spot until it is reachable */
      const go = () => {
        if (stopped) return;
        window.scrollTo(0, spot);
        const there = Math.abs(window.scrollY - spot) < 4;
        if ((!there || Date.now() - started < 1200) && Date.now() - started < 4000) setTimeout(go, 120);
      };
      go();
    }

    document.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('a[href]')) save();
    }, true);
    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', save);
    document.addEventListener('pointerdown', save, true);
  } catch {
    /* without storage the homepage simply opens at the top */
  }
})();

/*
 * Sofa banner: the "Shop All" button goes to every product; clicking the photo itself opens the
 * outfit shown in it.
 */
(() => {
  const looks = { IMG_2690: 'haseena-ivory-1', IMG_2698: 'haseena-lilac-1' };
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const slide = event.target.closest("[id$='__slideshow_xaXW9V'] slideshow-slide");
    if (!slide || event.target.closest('a, button')) return;
    const source = slide.querySelector('img')?.currentSrc || slide.querySelector('img')?.src || '';
    const key = Object.keys(looks).find((name) => source.includes(name));
    if (!key) return;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    window.location.href = root + 'products/' + looks[key];
  });
})();

/*
 * Collection pages: the title with the number of styles beside it (taken from the theme's own
 * "N items" count, which is hidden), and quick links to the neighbouring styles underneath.
 */
(() => {
  const main = document.querySelector('main');
  if (!main || !(main.dataset.template || '').startsWith('collection')) return;
  const block = main.querySelector("[id$='__section'] .text-block.h2");
  const title = block?.querySelector('h1, h2');
  if (!block || !title || block.classList.contains('hermyne-collection-head')) return;
  block.classList.add('hermyne-collection-head');

  /* the style links are drawn by the page; they belong straight under the title */
  const styles = main.querySelector('.hermyne-styles');
  const FAMILY = 'hermyne-style-family';
  let filtering = false;
  try {
    if (styles) {
      const own = styles.dataset.own === 'true';
      /* on a style page the links only appear while filtering from "Shop All" */
      filtering = own || sessionStorage.getItem(FAMILY) === styles.dataset.parentHandle;
      if (own) sessionStorage.removeItem(FAMILY);
    }
  } catch {
    filtering = styles?.dataset.own === 'true';
  }
  if (styles && (styles.querySelectorAll('a').length < 2 || !filtering)) {
    styles.remove(); /* a single link is not a choice */
  } else if (styles) {
    block.after(styles);
    styles.hidden = false;
    if (styles.dataset.own !== 'true' && styles.dataset.parentTitle) title.textContent = styles.dataset.parentTitle;
    styles.querySelector('.is-current')?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }

  const tag = document.createElement('span');
  tag.className = 'hermyne-collection-head__count';
  block.appendChild(tag);

  /* Sort sits at the right of the title row; choosing one reloads the list in that order */
  const sort = main.querySelector('.hermyne-sort-by');
  if (sort) {
    block.appendChild(sort);
    sort.hidden = false;
    const select = sort.querySelector('select');
    const go = (value) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    };

    /* our own menu instead of the browser's grey list */
    const holder = document.createElement('div');
    holder.className = 'hermyne-sort-by is-custom';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hermyne-sort-by__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    const current = select.options[select.selectedIndex] || select.options[0];
    button.innerHTML = '<span class="hermyne-sort-by__label">Sort</span><span class="hermyne-sort-by__value"></span><span class="hermyne-sort-by__caret" aria-hidden="true"></span>';
    button.querySelector('.hermyne-sort-by__value').textContent = current.textContent.trim();

    const menu = document.createElement('div');
    menu.className = 'hermyne-sort-by__menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;
    [...select.options].forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'hermyne-sort-by__option';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(option.selected));
      item.textContent = option.textContent.trim();
      item.addEventListener('click', () => (option.selected ? close() : go(option.value)));
      menu.appendChild(item);
    });

    const close = () => {
      menu.hidden = true;
      button.setAttribute('aria-expanded', 'false');
    };
    button.addEventListener('click', () => {
      menu.hidden = !menu.hidden;
      button.setAttribute('aria-expanded', String(!menu.hidden));
    });
    document.addEventListener('click', (event) => {
      if (!holder.contains(event.target)) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    holder.append(button, menu);
    sort.replaceWith(holder);
  }
  let watcher = null;
  const watch = () => {
    watcher?.disconnect();
    watcher = new MutationObserver(() => read());
    watcher.observe(main.querySelector('results-list') || main, { childList: true, subtree: true, characterData: true });
  };
  const read = () => {
    const count = main.querySelector('.products-count-wrapper');
    const number = parseInt((count?.textContent || '').replace(/[^0-9]/g, ''), 10);
    tag.hidden = !number;
    if (number) tag.textContent = number + (number === 1 ? ' style' : ' styles');
  };
  /*
   * The style links filter the page in place: the heading and links stay put and only the
   * products change. The address changes to the chosen style so "load more" and Sort keep
   * working on it; if anything goes wrong the link is simply followed as a normal page.
   */
  if (styles && styles.isConnected) {
    styles.addEventListener('click', async (event) => {
      const chip = event.target instanceof Element ? event.target.closest('.hermyne-styles__chip') : null;
      if (!chip || event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      if (chip.classList.contains('is-current') || styles.getAttribute('aria-busy') === 'true') return;
      const target = new URL(chip.href, window.location.href);
      /* a link that leaves the store is followed as a normal link, never loaded into the page */
      if (target.origin !== window.location.origin) {
        window.location.assign(target.toString());
        return;
      }
      const sortBy = new URL(window.location.href).searchParams.get('sort_by');
      if (sortBy) target.searchParams.set('sort_by', sortBy);
      try {
        sessionStorage.setItem(FAMILY, styles.dataset.parentHandle || '');
      } catch {
        /* without storage the next full load of a style page is simply plain */
      }
      const current = main.querySelector('results-list');
      styles.setAttribute('aria-busy', 'true');
      current?.classList.add('hermyne-filtering');
      styles.querySelectorAll('.hermyne-styles__chip').forEach((other) => {
        other.classList.toggle('is-current', other === chip);
        if (other === chip) other.setAttribute('aria-current', 'page');
        else other.removeAttribute('aria-current');
      });
      try {
        const reply = await fetch(target.toString(), { headers: { Accept: 'text/html' } });
        if (!reply.ok) throw new Error('not found');
        const page = new DOMParser().parseFromString(await reply.text(), 'text/html');
        const fresh = page.querySelector('results-list');
        if (!fresh || !current) throw new Error('no list');
        current.replaceWith(document.importNode(fresh, true));
        history.pushState({ hermyneStyle: true }, '', target.toString());
        if (page.title) document.title = page.title;
        if (styles.dataset.parentTitle) title.textContent = styles.dataset.parentTitle;
        read();
        watch();
      } catch (error) {
        window.location.assign(target.toString());
        return;
      } finally {
        styles.removeAttribute('aria-busy');
      }
    });
    window.addEventListener('popstate', () => window.location.reload());
  }

  read();
  /* filters redraw the list in place: keep the tag in step */
  watch();
})();

/*
 * Photo collage: moves on its own like a train. The whole collage glides sideways without
 * stopping; a second copy follows right behind it so the loop has no end or jump.
 * Slows to a stop while the pointer is over it; stays still for reduced motion.
 */
(() => {
  const collage = document.querySelector('.hermyne-collage');
  if (!collage || collage.closest('.hermyne-collage-train')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* every photo is fetched up front: lazy photos that sit off to the side never load on phones */
  collage.querySelectorAll('img').forEach((image) => {
    image.loading = 'eager';
    if (!image.complete || !image.naturalWidth) image.src = image.currentSrc || image.src;
  });

  const train = document.createElement('div');
  train.className = 'hermyne-collage-train';
  const track = document.createElement('div');
  track.className = 'hermyne-collage-train__track';
  collage.before(train);
  train.appendChild(track);
  track.appendChild(collage);

  const copy = collage.cloneNode(true);
  copy.setAttribute('aria-hidden', 'true');
  copy.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
  copy.querySelectorAll('img').forEach((image) => image.setAttribute('loading', 'eager'));
  [...copy.attributes].forEach((attribute) => {
    if (attribute.name.startsWith('data-shopify')) copy.removeAttribute(attribute.name);
  });
  track.appendChild(copy);

  /* Driven frame by frame rather than by a CSS animation, so nothing can pause or restart it:
     it keeps one steady speed and wraps round exactly one collage-width at a time. */
  /* Speed follows the screen: a photo crosses a wide monitor in about the same time as a phone.
     Pixels a second; never slower than 70. */
  const speed = () => Math.max(70, window.innerWidth * 0.06);
  let offset = 0;
  let last = 0;
  const step = (now) => {
    if (last) {
      const unit = copy.getBoundingClientRect().left - collage.getBoundingClientRect().left;
      /* long gaps (tab in the background) do not turn into a jump */
      offset += speed() * Math.min((now - last) / 1000, 0.05);
      if (unit > 0 && offset >= unit) offset -= unit;
      track.style.transform = 'translate3d(' + -offset.toFixed(2) + 'px, 0, 0)';
    }
    last = now;
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
})();

/*
 * Customer photos: product names under each photo are written the tidy way
 * ("Black Corset and Skirt", "Gulabi Co-ord Set"), whatever case the review app sends.
 */
(() => {
  const small = new Set(['and', 'with', 'in', 'of', 'for', 'the', 'a']);
  const tidy = (text) =>
    text
      .trim()
      .split(/\s+/)
      .map((word, index) => {
        const lower = word.toLowerCase();
        if (/^co-?ord$/.test(lower)) return 'Co-ord';
        if (index > 0 && small.has(lower)) return lower;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  const run = () => {
    document.querySelectorAll('.jdgm-cards-carousel .jdgm-product-name:not([data-tidy])').forEach((name) => {
      const holder = name.querySelector('a, span') || name;
      if (holder.children.length) return;
      name.dataset.tidy = '';
      holder.textContent = tidy(holder.textContent);
    });
  };
  run();
  let wait = null;
  new MutationObserver(() => {
    clearTimeout(wait);
    wait = setTimeout(run, 300);
  }).observe(document.body, { childList: true, subtree: true });
})();

/*
 * WhatsApp chat panel: the button says what it does ("Chat on WhatsApp").
 * The chat app draws the panel a moment after the page loads, so wait for it.
 */
(() => {
  const label = () => {
    const button = document.querySelector('#df-btn-cont .df-bottombar-btn');
    if (!button || button.dataset.hermyne) return !!button;
    button.dataset.hermyne = '';
    button.textContent = 'Chat on WhatsApp';
    return true;
  };
  if (label()) return;
  let tries = 0;
  const timer = setInterval(() => {
    if (label() || ++tries > 40) clearInterval(timer);
  }, 500);
})();

/*
 * Phone menu: the menu button opens Hermyne's own menu instead of the theme drawer.
 * It opens in place under the real header, which stays on top exactly as it is; the menu button
 * becomes a close button, and the other header icons still work (they close the menu first).
 */
(() => {
  const menu = document.getElementById('HermyneMenu');
  if (!menu) return;
  const phone = window.matchMedia('(max-width: 749px)');
  let opener = null;

  const header = () => document.querySelector('header-component');
  const isLight = (color) => {
    const parts = (color.match(/[\d.]+/g) || []).map(Number);
    return parts.length >= 3 && (parts[0] * 299 + parts[1] * 587 + parts[2] * 114) / 1000 > 140;
  };

  const open = () => {
    const bar = header();
    if (bar) {
      /* the menu starts where the header ends */
      menu.style.setProperty('--hermyne-menu-top', Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) + 'px');
      /* over a photo the header draws white; on the menu's light background it must read dark */
      const logo = bar.querySelector('.header-logo');
      bar.toggleAttribute('data-hermyne-menu-flip', !!logo && isLight(getComputedStyle(logo).color));
    }
    /* the two photos are fetched the first time the menu is opened, not on every page load */
    menu.querySelectorAll('img[loading="lazy"]').forEach((image) => (image.loading = 'eager'));
    menu.hidden = false;
    const sheet = menu.querySelector('.hermyne-menu__sheet');
    if (sheet) sheet.scrollTop = 0;
    requestAnimationFrame(() => menu.classList.add('is-open'));
    document.body.classList.add('hermyne-menu-open');
    opener?.setAttribute?.('aria-label', 'Close menu');
  };
  const close = () => {
    menu.classList.remove('is-open');
    document.body.classList.remove('hermyne-menu-open');
    menu.hidden = true;
    header()?.removeAttribute('data-hermyne-menu-flip');
    opener?.setAttribute?.('aria-label', 'Menu');
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('header-drawer summary, .header__icon--menu');
    if (trigger && phone.matches) {
      event.preventDefault();
      event.stopPropagation();
      opener = trigger;
      if (menu.hidden) open();
      else close();
      return;
    }
    /* search, account, bag or the logo in the header: the menu steps aside and they do their job */
    if (!menu.hidden && event.target.closest('header-component')) close();
  }, true);

  /* A tap on the dimmed page below the sheet closes the menu. The listeners sit on the menu
     itself, because iPhones do not pass taps on a plain area up to the page. */
  const dismiss = (event) => {
    if (event.target !== menu || menu.hidden) return;
    event.preventDefault();
    close();
  };
  menu.addEventListener('click', dismiss);
  menu.addEventListener('touchend', dismiss);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !menu.hidden) close();
  });
  phone.addEventListener?.('change', () => {
    if (!phone.matches && !menu.hidden) close();
  });
  addEventListener('pageshow', () => {
    if (!menu.hidden) close();
  });
})();

/*
 * Customer photos: a click on a card goes to the product it shows, instead of opening the
 * review app's pop-up. Cards with no product link do nothing.
 */
(() => {
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const card = event.target.closest('.jdgm-cards-carousel .jdgm-card');
    if (!card) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const link = card.querySelector('.jdgm-product-name a[href*="/products/"]');
    if (!link) return;
    try {
      const url = new URL(link.getAttribute('href'), window.location.href);
      if (url.origin !== window.location.origin && !/(^|\.)hermyne\.in$/.test(url.hostname)) return;
      /* a path on this store only ("/products/..."), never "//another-site" */
      if (!/^\/(?!\/)/.test(url.pathname)) return;
      window.location.assign(url.pathname + url.search);
    } catch {
      /* an odd link is simply not followed */
    }
  }, true);
})();

/*
 * Video row and its pop-up (the video app): a product leads to its product page.
 * In the row, the little product strip under a video (photo, name, price) opens that product;
 * the video itself still opens the pop-up. Inside the pop-up, the product photo and name open the
 * product page too, and "Size" gets a size chart link. The app's own Add to cart and "details"
 * button work as they did.
 */
(() => {
  const go = (handle) => {
    if (!handle || !/^[a-z0-9][a-z0-9-_]*$/i.test(handle)) return false;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    window.location.assign(root + 'products/' + handle);
    return true;
  };
  const handleFrom = (el) => {
    if (!el) return '';
    if (el.dataset && el.dataset.handle) return el.dataset.handle;
    const link = el.querySelector('a[href*="/products/"]');
    const match = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  };

  /* Size chart: the same chart the product page shows, fetched from that page and shown in the
     site's own centred panel. If the product has no chart, the link opens the product page. */
  const charts = new Map();
  const chartOf = (handle) => {
    if (!charts.has(handle)) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      charts.set(
        handle,
        fetch(root + 'products/' + handle, { headers: { Accept: 'text/html' } })
          .then((reply) => (reply.ok ? reply.text() : ''))
          .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[class*="wsp-size-chart-content-"]'))
          .catch(() => null)
      );
    }
    return charts.get(handle);
  };
  let panel = null;
  const openChart = async (handle) => {
    if (!handle || !/^[a-z0-9][a-z0-9-_]*$/i.test(handle)) return;
    const content = await chartOf(handle);
    if (!content) {
      go(handle);
      return;
    }
    if (!panel) {
      panel = document.createElement('dialog');
      panel.className = 'hermyne-info-dialog';
      const head = document.createElement('div');
      head.className = 'hermyne-info-dialog__head';
      const title = document.createElement('h2');
      title.className = 'hermyne-info-dialog__title';
      title.textContent = 'Size chart';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'hermyne-info-dialog__close';
      close.setAttribute('aria-label', 'Close');
      close.textContent = '\u2715';
      head.append(title, close);
      const body = document.createElement('div');
      body.className = 'hermyne-info-dialog__body';
      panel.append(head, body);
      panel.addEventListener('click', (event) => {
        if (event.target === panel || (event.target instanceof Element && event.target.closest('.hermyne-info-dialog__close'))) panel.close();
      });
      document.body.appendChild(panel);
    }
    const body = panel.querySelector('.hermyne-info-dialog__body');
    body.textContent = '';
    const copy = document.importNode(content, true);
    copy.querySelectorAll('script').forEach((node) => node.remove());
    copy.querySelectorAll('img').forEach((img) => {
      img.removeAttribute('loading');
      img.style.cssText = 'width:100%;height:auto;border-radius:12px';
    });
    body.appendChild(copy);
    if (!panel.open) panel.showModal();
  };

  /* The product's own photos at the top of the pop-up's product side (laptops and desktops),
     taken from the store's standard product data. */
  const products = new Map();
  const productOf = (handle) => {
    if (!products.has(handle)) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      products.set(
        handle,
        fetch(root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
          .then((reply) => (reply.ok ? reply.json() : null))
          .catch(() => null)
      );
    }
    return products.get(handle);
  };
  const sized = (src, width) => {
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  };
  /* The app rebuilds the product side every time a video opens, which removes anything added
     to it. So the photos are put back whenever they are missing, and their space is laid out at
     once (four empty frames) so the side does not start small and then grow as photos arrive. */
  const BLANK = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
  const addGalleries = () => {
    document.querySelectorAll('.rf-lightbox-wrapper .rf-lightbox-single-product[data-handle]').forEach((box) => {
      const infos = box.querySelector('.rf-product-infos');
      if (!infos) return;
      const has = !!infos.querySelector(':scope > .hermyne-rf-gallery');
      box.classList.toggle('hermyne-rf-has-gallery', has);
      /* only where the product side is on screen (phones show the product under the video instead) */
      if (has || infos.offsetParent === null) return;
      const handle = box.dataset.handle;
      if (!/^[a-z0-9][a-z0-9-_]*$/i.test(handle)) return;
      const strip = document.createElement('div');
      strip.className = 'hermyne-rf-gallery';
      for (let index = 0; index < 4; index += 1) {
        const img = document.createElement('img');
        img.alt = '';
        img.decoding = 'async';
        img.src = BLANK;
        strip.appendChild(img);
      }
      infos.insertBefore(strip, infos.firstChild);
      box.classList.add('hermyne-rf-has-gallery');
      productOf(handle).then((product) => {
        const images = (product && Array.isArray(product.images) ? product.images : []).slice(0, 4);
        const frames = [...strip.querySelectorAll('img')];
        if (!images.length) {
          /* no photos to show: give the side back to the app's own small picture */
          strip.remove();
          box.classList.remove('hermyne-rf-has-gallery');
          return;
        }
        frames.forEach((img, index) => {
          if (images[index]) img.src = sized(images[index], index === 0 ? 700 : 300);
          else img.remove();
        });
      });
    });
  };
  /* while a video pop-up is open, keep checking: moving to the next video does not always change the page */
  setInterval(() => {
    if (document.querySelector('.pswp_rf--open')) {
      addGalleries();
      addChartLinks();
    }
  }, 400);

  /* the app builds each product's form when its video opens: add the link to the "Size" label then */
  const addChartLinks = () => {
    document.querySelectorAll('.rf-lightbox-wrapper .rf-form__label:not(.hermyne-size-row), #rf-mobile-product .rf-form__label:not(.hermyne-size-row)').forEach((label) => {
      if (!/size/i.test(label.textContent)) return;
      const holder = label.closest('.rf-lightbox-single-product, .rf-product-item, [data-handle]');
      const handle = handleFrom(holder);
      if (!handle) return;
      label.classList.add('hermyne-size-row');
      const link = document.createElement('button');
      link.type = 'button';
      link.className = 'hermyne-size-chart-link hermyne-rf-chart';
      link.dataset.handle = handle;
      link.textContent = 'Size chart';
      label.appendChild(link);
    });
  };
  let chartWait = null;
  new MutationObserver(() => {
    clearTimeout(chartWait);
    chartWait = setTimeout(() => {
      addChartLinks();
      addGalleries();
    }, 200);
  }).observe(document.body, { childList: true, subtree: true });

  /*
   * Phones: the product shown under a video opens the site's own quick-add sheet (the one "+ Add"
   * opens), not the app's. The sheet is driven through a quick-add component already on the page,
   * using its own fetch and its own way of filling the sheet. If none is on the page, or anything
   * is missing, the product page opens instead.
   */
  const phone = window.matchMedia('(max-width: 749px)');
  const openQuickAdd = async (handle) => {
    if (!handle || !/^[a-z0-9][a-z0-9-_]*$/i.test(handle)) return;
    const borrowed = document.querySelector('quick-add-component');
    const sheet = document.getElementById('quick-add-dialog');
    if (!borrowed || !sheet || typeof borrowed.fetchProductPage !== 'function' || typeof borrowed.updateQuickAddModal !== 'function' || typeof sheet.showDialog !== 'function') {
      go(handle);
      return;
    }
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const url = new URL(root + 'products/' + handle, window.location.origin).toString();
    const loader = document.getElementById('hermyne-loader');
    loader?.classList.add('is-showing', 'is-light');
    loader?.classList.remove('is-hidden');
    try {
      const page = await borrowed.fetchProductPage(url);
      const grid = page && page.querySelector('[data-product-grid-content]');
      if (!grid) throw new Error('no product content');
      const name = (page.querySelector('.product-details h1, [data-product-grid-content] h1')?.textContent || '').trim();
      await borrowed.updateQuickAddModal(grid.cloneNode(true));
      const content = document.getElementById('quick-add-modal-content');
      /* the borrowed component writes its own product's name and link into the heading: correct both */
      const heading = content?.querySelector('.product-header a');
      if (heading) {
        if (name) heading.textContent = name;
        heading.href = url;
      }
      content?.querySelector('variant-picker')?.updateVariantPicker?.(grid);
      sheet.showDialog();
    } catch (error) {
      go(handle);
    } finally {
      loader?.classList.add('is-hidden');
      loader?.classList.remove('is-showing', 'is-light');
    }
  };

  /* Quick-add sheet on phones: a round "details" button beside Add to cart that opens the full
     product page, like the one in the video pop-up on desktop. Re-added whenever the sheet is refilled. */
  const addSheetInfo = () => {
    if (!phone.matches) return;
    const content = document.getElementById('quick-add-modal-content');
    const buttons = content?.querySelector('.product-form-buttons');
    if (!buttons || buttons.querySelector('.hermyne-sheet-info')) return;
    const link = content.querySelector('.product-header a[href*="/products/"], a[href*="/products/"]');
    if (!link) return;
    const info = document.createElement('a');
    info.className = 'hermyne-sheet-info';
    info.href = link.getAttribute('href');
    info.setAttribute('aria-label', 'See full product details');
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('width', '22');
    icon.setAttribute('height', '22');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '1.8');
    icon.setAttribute('stroke-linecap', 'round');
    icon.setAttribute('aria-hidden', 'true');
    const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    ring.setAttribute('cx', '12');
    ring.setAttribute('cy', '12');
    ring.setAttribute('r', '9');
    const stem = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    stem.setAttribute('d', 'M12 11v6M12 7.5v.01');
    icon.append(ring, stem);
    info.appendChild(icon);
    const pay = buttons.querySelector('.accelerated-checkout-block');
    buttons.insertBefore(info, pay || null);
  };
  const sheetContent = document.getElementById('quick-add-modal-content');
  if (sheetContent) {
    let infoWait = null;
    new MutationObserver(() => {
      clearTimeout(infoWait);
      infoWait = setTimeout(addSheetInfo, 120);
    }).observe(sheetContent, { childList: true, subtree: true });
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    /* phones, inside the video pop-up: the product under the video opens the quick-add sheet */
    const under = event.target.closest('.rf-mobile-info .rf-product-item, .rf-mobile-info .rf-product-mopen');
    if (under && phone.matches) {
      const item = under.closest('.rf-product-item') || under;
      const handle = handleFrom(item) || handleFrom(under.closest('.rf-lightbox-wrapper')?.querySelector('[data-handle]'));
      if (handle) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openQuickAdd(handle);
        return;
      }
    }

    /* row: the click lands on the app's full-card link, so compare it with where the strip is drawn */
    const card = event.target.closest('.reelfy-wrapper .reelfy_card');
    if (card) {
      const strip = card.querySelector('.reelfy_card_product');
      if (strip) {
        const box = strip.getBoundingClientRect();
        const inside =
          event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
        if (inside && go(handleFrom(strip))) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }
      return;
    }

    /* pop-up: the size chart link beside "Size" */
    const chartLink = event.target.closest('.hermyne-rf-chart');
    if (chartLink) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openChart(chartLink.dataset.handle);
      return;
    }

    /* pop-up: a small product photo swaps places with the large one */
    const small = event.target.closest('.hermyne-rf-gallery img:not(:first-child)');
    if (small) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const large = small.parentElement.querySelector('img');
      const held = large.src;
      large.src = small.src;
      small.src = held;
      return;
    }

    /* pop-up: product photo or name */
    const part = event.target.closest('.pswp_rf .rf-product-infos :is(.rf-product-img, .rf-product-title, .hermyne-rf-gallery img)');
    if (part) {
      const holder = part.closest('.rf-lightbox-single-product, .rf-product-item, [data-handle]');
      if (go(handleFrom(holder))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }
  }, true);
})();

/*
 * Customer photos on phones: the review app listens for touches to run its own carousel, which
 * fights the finger. The row is a plain sideways scroller, so those touches stay with the browser.
 */
(() => {
  const free = () => {
    document.querySelectorAll('.jdgm-cards-carousel .jdgm-cards-wrapper:not([data-free])').forEach((row) => {
      row.dataset.free = '';
      ['touchstart', 'touchmove', 'touchend', 'pointerdown', 'pointermove', 'pointerup', 'mousedown'].forEach((type) => {
        row.addEventListener(type, (event) => event.stopPropagation(), { capture: true, passive: true });
      });
    });
  };
  free();
  let wait = null;
  new MutationObserver(() => {
    clearTimeout(wait);
    wait = setTimeout(free, 400);
  }).observe(document.body, { childList: true, subtree: true });
})();

/*
 * Links written with the store's full address (in menus and product text) are followed on the
 * site the shopper is already on, so they keep the same header, footer and look.
 */
(() => {
  const hosts = ['hermyne.in', 'www.hermyne.in', 'hermynetest.myshopify.com'];
  const fix = (root) => {
    root.querySelectorAll('a[href^="http"]').forEach((link) => {
      try {
        const url = new URL(link.href);
        if (!hosts.includes(url.hostname) || url.hostname === location.hostname) return;
        if (!/^\/(?!\/)/.test(url.pathname)) return;
        link.setAttribute('href', url.pathname + url.search + url.hash);
        if (link.target === '_blank') link.removeAttribute('target');
      } catch {
        /* leave anything unusual alone */
      }
    });
  };
  fix(document);
  let wait = null;
  new MutationObserver(() => {
    clearTimeout(wait);
    wait = setTimeout(() => fix(document), 500);
  }).observe(document.body, { childList: true, subtree: true });
})();

/*
 * Carousels always come back to playing.
 * The theme pauses a carousel when it is swiped or when the tab goes to the background, and
 * then leaves it paused. There is no pause button any more, so a few seconds after the last
 * touch every auto-playing carousel is set going again.
 */
(() => {
  const touched = new WeakMap();
  ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) => {
    document.addEventListener(type, (event) => {
      const component = event.target instanceof Element ? event.target.closest('slideshow-component[autoplay]') : null;
      if (component) touched.set(component, Date.now());
    }, { passive: true, capture: true });
  });
  setInterval(() => {
    if (document.hidden) return;
    document.querySelectorAll('slideshow-component[autoplay][paused]').forEach((component) => {
      if (component.hasAttribute('dragging')) return;
      if (Date.now() - (touched.get(component) || 0) < 3500) return;
      try {
        component.play();
      } catch {
        /* a carousel that cannot play is left as it is */
      }
    });
  }, 1500);
})();

/*
 * Safety net: the page must never stay frozen.
 * The theme locks page scrolling while a pop-up or the cart is open. If one of those is closed
 * by anything other than its own close button, the lock can be left behind and the page looks
 * hung. Whenever nothing is open, any leftover lock is released.
 */
(() => {
  setInterval(() => {
    const body = document.body;
    if (body.style.position !== 'fixed') return;
    if (document.querySelector('dialog[open]')) return;
    if (body.classList.contains('hermyne-menu-open')) return;
    const top = parseInt(body.style.top || '0', 10);
    body.style.position = '';
    body.style.width = '';
    body.style.top = '';
    if (top) window.scrollTo({ top: -top, behavior: 'instant' });
  }, 700);
})();

/*
 * Small product photos switch the main photo wherever the gallery appears.
 * The gallery's own script wires them up when a product page loads; inside the quick-add sheet
 * the gallery is put in afterwards and that wiring never runs, so the small photos did nothing.
 * One listener on the page covers both.
 */
(() => {
  document.addEventListener('click', (event) => {
    const thumb = event.target instanceof Element ? event.target.closest('[data-gallery-thumbnail-index]') : null;
    if (!thumb) return;
    const gallery = thumb.closest('media-gallery') || thumb.closest('.product-information__media');
    if (!gallery || gallery.hasAttribute('data-thumbnails-initialized')) return; /* already wired by the page */
    event.preventDefault();
    const index = Number(thumb.dataset.galleryThumbnailIndex);
    const native = gallery.querySelectorAll('.gallery-native-thumbnail-controls .slideshow-controls__thumbnail')[index];
    const slideshow = gallery.querySelector('slideshow-component');
    if (native) native.click();
    else if (slideshow && typeof slideshow.select === 'function') slideshow.select(index);
    /* In the quick-add sheet the photos are stacked rather than slid sideways, so "selecting"
       one does not bring it into view there. Show the chosen photo and hide the others. */
    if (gallery.closest('.quick-add-modal__content')) {
      gallery.querySelectorAll('slideshow-slide').forEach((slide, position) => {
        slide.style.setProperty('display', position === index ? 'flex' : 'none', 'important');
        slide.style.setProperty('opacity', '1', 'important');
      });
    }
    gallery.querySelectorAll('[data-gallery-thumbnail-index]').forEach((other) => {
      const on = other === thumb;
      other.classList.toggle('is-active', on);
      other.setAttribute('aria-selected', String(on));
    });
  });
})();

/*
 * Wording tidy-up (display only; the store's own data is not changed).
 *  - size buttons read the same everywhere: S, M, L, XL (not "Small", "Xl", "Xs/Small")
 *  - product names under the videos use the tidy form ("Mocha Co-ord Set")
 *  - two slips in product descriptions are corrected ("The is a…", "measuring , adding")
 */
(() => {
  const sizes = {
    'x-small': 'XS', xsmall: 'XS', 'extra-small': 'XS', xs: 'XS',
    small: 'S', s: 'S',
    medium: 'M', m: 'M',
    large: 'L', l: 'L',
    'x-large': 'XL', xlarge: 'XL', 'extra-large': 'XL', xl: 'XL',
    'xx-large': 'XXL', xxlarge: 'XXL', xxl: 'XXL',
    'xs/small': 'XS/S', 'xs/s': 'XS/S', 'xs-s': 'XS/S',
    'large/ xlarge': 'L/XL', 'large/xlarge': 'L/XL', 'l-xl': 'L/XL', 'l/xl': 'L/XL', 'large/': 'L',
  };
  const small = new Set(['and', 'with', 'in', 'of', 'for', 'the', 'a']);
  const titleCase = (text) =>
    text.trim().split(/\s+/).map((word, index) => {
      const lower = word.toLowerCase();
      if (/^co-?ord(-set)?$/.test(lower)) return lower.endsWith('set') ? 'Co-ord Set' : 'Co-ord';
      if (lower === 'mens') return "Men's";
      if (index > 0 && small.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    }).join(' ');

  const leaves = (root, visit) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) visit(node);
  };

  const run = () => {
    /* size buttons and the size shown on a cart line */
    document.querySelectorAll('variant-picker label, variant-picker option, .sticky-add-to-cart__bar option').forEach((holder) => {
      if (holder.dataset.hermyneSize) return;
      leaves(holder, (node) => {
        const raw = node.nodeValue.trim();
        if (!raw) return;
        const [name, ...rest] = raw.split(' - ');
        const tidy = sizes[name.trim().toLowerCase()];
        if (tidy && tidy !== name.trim()) node.nodeValue = node.nodeValue.replace(name.trim(), tidy);
      });
      holder.dataset.hermyneSize = '';
    });

    /* product names on the video tiles */
    document.querySelectorAll('.reelfy-wrapper .reelfy_card_product__title :is(a, span), .reelfy-wrapper .reelfy_card_product__title').forEach((el) => {
      if (el.children.length || el.dataset.hermyneName) return;
      const text = el.textContent.trim();
      if (!text || text.length > 60 || /^rs\.?/i.test(text) || /[#@\[\(]/.test(text)) return;
      el.dataset.hermyneName = '';
      el.textContent = titleCase(text);
    });

    /* description slips */
    document.querySelectorAll('.product-details, .hermyne-info-dialog').forEach((area) => {
      if (area.dataset.hermyneCopy) return;
      area.dataset.hermyneCopy = '';
      leaves(area, (node) => {
        let text = node.nodeValue;
        if (/^\s*The is a /.test(text)) text = text.replace(/The is a /, 'This is a ');
        if (/dupatta measuring\s*$/.test(text)) text = text.replace(/ measuring\s*$/, '');
        if (/^\s*Fabric: armani satin/i.test(text)) text = text.replace(/armani satin/i, 'Armani satin');
        if (text !== node.nodeValue) node.nodeValue = text;
      });
    });
  };

  run();
  let wait = null;
  new MutationObserver(() => {
    clearTimeout(wait);
    wait = setTimeout(run, 350);
  }).observe(document.body, { childList: true, subtree: true });
})();

/*
 * Cart page: "Keep shopping" goes back to the page the shopper was on.
 * If they arrived from elsewhere (a link, a new tab) it goes to the shop instead.
 */
(() => {
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-hermyne-back]') : null;
    if (!link) return;
    let sameSite = false;
    try {
      sameSite = !!document.referrer && new URL(document.referrer).origin === location.origin && new URL(document.referrer).pathname !== location.pathname;
    } catch {
      sameSite = false;
    }
    if (sameSite && history.length > 1) {
      event.preventDefault();
      history.back();
    }
  });
})();


/* Picking a page from the header or the phone menu ends any "filtering from Shop All" state */
(() => {
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('header-component a, .hermyne-menu a, .menu-drawer a')) {
      try {
        sessionStorage.removeItem('hermyne-style-family');
      } catch {
        /* nothing to clear */
      }
    }
  }, true);
})();

/*
 * Coupon box in the cart: PREPAID15 is given on the Razorpay payment screen, so the cart cannot
 * apply it. For that one code the box explains this instead of showing "cannot be applied".
 */
(() => {
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof Element ? event.target.closest('.cart-discount__form') : null;
      if (!form) return;
      const box = form.closest('cart-discount-component');
      const input = form.querySelector('input[name="discount"]');
      if (!box || !(input instanceof HTMLInputElement)) return;
      box.toggleAttribute('data-hermyne-prepaid', input.value.trim().toUpperCase() === 'PREPAID15');
    },
    true
  );
})();
